import React from "react";
import { FadeIn } from "components/shared/FadeIn";
import type { Certification } from "types";

interface Props {
  certificate: Certification;
}

const CertificationCard: React.FC<Props> = ({ certificate }) => {
  return (
    <FadeIn duration={800}>
      <a
        href={certificate.certificate_link}
        target="_blank"
        rel="noopener noreferrer"
        className="group block h-full"
      >
        <div className="relative h-full border border-border bg-bg-elevated rounded-lg p-6 transition-all duration-300 hover:border-border-hover hover:shadow-card-hover">
          {/* Top accent line */}
          <div
            className="absolute top-0 left-0 right-0 h-px"
            style={{
              background: `linear-gradient(90deg, ${certificate.color_code}, transparent)`,
            }}
          />

          {/* Verified badge */}
          <div className="absolute top-4 right-4">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono tracking-wider uppercase border border-accent/20 rounded text-accent bg-accent-dim">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              Verified
            </span>
          </div>

          {/* Logo */}
          <div className="flex items-center justify-center w-14 h-14 mb-5 border border-border bg-bg-surface rounded">
            {certificate.logo_path && certificate.logo_path.trim() !== "" ? (
              <img
                className="object-contain w-8 h-8"
                src={require(`../../assests/images/${certificate.logo_path}`)}
                alt={certificate.alt_name}
              />
            ) : certificate.iconClass ? (
              <i
                className={`${certificate.iconClass} text-2xl`}
                style={{ color: certificate.color_code }}
              />
            ) : (
              <span
                className="text-xl font-display font-bold"
                style={{ color: certificate.color_code }}
              >
                {certificate.alt_name?.charAt(0) || "C"}
              </span>
            )}
          </div>

          {/* Content */}
          <h3 className="text-base font-display font-bold tracking-tight text-text-primary mb-2 group-hover:text-accent transition-colors">
            {certificate.title}
          </h3>

          <div className="flex items-center gap-2 mb-3">
            <svg
              className="w-3 h-3 text-text-muted"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
            </svg>
            <span className="text-xs font-mono text-text-secondary">
              {certificate.subtitle}
            </span>
          </div>

          {/* View link */}
          <div className="flex items-center gap-1 text-xs font-mono text-accent group-hover:underline">
            View Certificate
            <svg
              className="w-3 h-3 transition-transform group-hover:translate-x-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </div>
        </div>
      </a>
    </FadeIn>
  );
};

export default CertificationCard;
